/**
 * Helpers for the "Last sign-in" session-activity display (security control
 * A.12). They turn the raw login telemetry recorded by the backend
 * (`previousLoginAt` / `previousLoginIp` / `previousLoginUserAgent`) into a
 * human-readable summary so a user can spot an unfamiliar sign-in.
 */

/** Formats a login timestamp as e.g. "21 Jul 2026, 09:23". Returns '' if empty/invalid. */
export const formatSignInDateTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Best-effort browser + OS extraction from a user-agent string, e.g.
 * "Chrome (Windows)". Deliberately lightweight — enough for the user to
 * recognise their own device without pulling in a UA-parsing dependency.
 */
export const parseUserAgent = (userAgent?: string | null): string => {
  if (!userAgent) return '';

  const browserMatchers: Array<[RegExp, string]> = [
    [/edg/i, 'Edge'],
    [/opr|opera/i, 'Opera'],
    [/chrome|crios/i, 'Chrome'],
    [/firefox|fxios/i, 'Firefox'],
    [/safari/i, 'Safari'],
  ];
  const osMatchers: Array<[RegExp, string]> = [
    [/windows/i, 'Windows'],
    [/iphone|ipad|ipod/i, 'iOS'],
    [/mac os x|macintosh/i, 'macOS'],
    [/android/i, 'Android'],
    [/linux/i, 'Linux'],
  ];

  const browser = browserMatchers.find(([re]) => re.test(userAgent))?.[1];
  const os = osMatchers.find(([re]) => re.test(userAgent))?.[1];

  if (browser && os) return `${browser} (${os})`;
  return browser || os || '';
};

/**
 * Builds the "Last signed in" summary line from the previous session's
 * telemetry, omitting any pieces the backend didn't capture.
 * Returns '' when there is no previous session (e.g. first-ever sign-in).
 */
export const buildLastSignInSummary = (params: {
  at?: string | null;
  ip?: string | null;
  userAgent?: string | null;
}): string => {
  const when = formatSignInDateTime(params.at);
  if (!when) return '';

  const parts = [when];
  if (params.ip) parts.push(`from ${params.ip}`);
  const device = parseUserAgent(params.userAgent);
  if (device) parts.push(`on ${device}`);
  return parts.join(' ');
};
